import React from 'react';

// Minimal inline-SVG charts for the analytics screens — no chart library, solid colours,
// same look as the prototype (bars for quantities, lines for ratios).

export interface Point { label: string; value: number | null }

const PAD = { top: 12, right: 12, bottom: 28, left: 44 };

const niceMax = (max: number): number => {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const n = max / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
};

export const BarChart: React.FC<{ data: Point[]; color?: string; height?: number; format?: (v: number) => string }> = ({ data, color = '#2f80ed', height = 220, format = v => String(v) }) => {
  const width = 640;
  const innerW = width - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const rawMax = Math.max(0, ...data.map(d => d.value ?? 0));
  const max = niceMax(rawMax);
  // Small or empty ranges need a decimal on the axis, otherwise 0.5 prints as "1".
  const fmtTick = (v: number) => (max < 10 ? v.toLocaleString('pl-PL', { maximumFractionDigits: 1 }) : format(v));
  const slot = data.length ? innerW / data.length : innerW;
  const barW = Math.min(48, slot * 0.6);
  const ticks = [0, 0.5, 1].map(f => f * max);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {ticks.map(tv => {
        const y = PAD.top + innerH - (tv / max) * innerH;
        return (
          <g key={tv}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke="#e2e8f0" />
            <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{fmtTick(tv)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const v = d.value ?? 0;
        const h = (v / max) * innerH;
        const x = PAD.left + i * slot + (slot - barW) / 2;
        const y = PAD.top + innerH - h;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={h} rx={3} fill={color}>
              <title>{`${d.label}: ${format(v)}`}</title>
            </rect>
            {data.length <= 16 && <text x={x + barW / 2} y={height - 10} textAnchor="middle" fontSize="10" fill="#64748b">{d.label}</text>}
          </g>
        );
      })}
    </svg>
  );
};

export interface Series { name: string; color: string; points: Point[] }

export const LineChart: React.FC<{ series: Series[]; height?: number; max?: number; format?: (v: number) => string }> = ({ series, height = 220, max: fixedMax, format = v => String(v) }) => {
  const width = 640;
  const innerW = width - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const labels = series[0]?.points.map(p => p.label) ?? [];
  const max = fixedMax ?? niceMax(Math.max(0, ...series.flatMap(s => s.points.map(p => p.value ?? 0))));
  const n = labels.length;
  const xFor = (i: number) => PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yFor = (v: number) => PAD.top + innerH - (v / max) * innerH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => f * max);
  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {ticks.map(tv => (
          <g key={tv}>
            <line x1={PAD.left} x2={width - PAD.right} y1={yFor(tv)} y2={yFor(tv)} stroke="#e2e8f0" />
            <text x={PAD.left - 6} y={yFor(tv) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{format(tv)}</text>
          </g>
        ))}
        {labels.map((l, i) => (n <= 16 || i % Math.ceil(n / 8) === 0) && (
          <text key={l} x={xFor(i)} y={height - 10} textAnchor="middle" fontSize="10" fill="#64748b">{l}</text>
        ))}
        {series.map(s => {
          const pts = s.points.map((p, i) => (p.value === null ? null : `${xFor(i)},${yFor(p.value)}`));
          // Break the line where a value is missing instead of drawing through the gap.
          const segments: string[] = [];
          let cur: string[] = [];
          pts.forEach(p => { if (p === null) { if (cur.length) segments.push(cur.join(' ')); cur = []; } else cur.push(p); });
          if (cur.length) segments.push(cur.join(' '));
          return (
            <g key={s.name}>
              {segments.map((seg, i) => <polyline key={i} points={seg} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" />)}
              {s.points.map((p, i) => p.value !== null && (
                <circle key={i} cx={xFor(i)} cy={yFor(p.value)} r={3.5} fill={s.color}>
                  <title>{`${p.label} · ${s.name}: ${format(p.value)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#475569', marginTop: 4 }}>
        {series.map(s => (
          <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: 'inline-block' }} />{s.name}
          </span>
        ))}
      </div>
    </div>
  );
};

/** Horizontal stacked bar: time structure (production / short stops / downtime / no data). */
export const StackedBar: React.FC<{ parts: { label: string; value: number; color: string }[]; format?: (v: number) => string }> = ({ parts, format = v => String(v) }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div style={{ display: 'flex', height: 26, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9' }}>
        {total > 0 && parts.map(p => p.value > 0 && (
          <div key={p.label} title={`${p.label}: ${format(p.value)}`} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: '#475569', marginTop: 8 }}>
        {parts.map(p => (
          <span key={p.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: p.color, display: 'inline-block' }} />
            {p.label} <b>{format(p.value)}</b> <span style={{ color: '#94a3b8' }}>{total > 0 ? `${((p.value / total) * 100).toFixed(1)}%` : ''}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------------------
// Time-based charts for the line history screen.

export interface TimePoint { t: number; v: number | null }
export interface TimeSeries { name: string; color: string; points: TimePoint[]; dashed?: boolean; width?: number }

/** X axis = time [unix s]. Optional horizontal band (e.g. diameter tolerance). */
export const TimeChart: React.FC<{
  series: TimeSeries[]; from: number; to: number; height?: number;
  min?: number; max?: number; band?: { from: number; to: number; color: string };
  format?: (v: number) => string; tickLabel: (t: number) => string;
}> = ({ series, from, to, height = 240, min: fixedMin, max: fixedMax, band, format = v => String(v), tickLabel }) => {
  const width = 960;
  const innerW = width - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const values = series.flatMap(s => s.points.map(p => p.v)).filter((v): v is number => v !== null);
  const dataMax = Math.max(...values, band?.to ?? -Infinity);
  const dataMin = Math.min(...values, band?.from ?? Infinity);
  const max = fixedMax ?? (values.length ? niceMax(dataMax) : 1);
  const min = fixedMin ?? (values.length ? Math.max(0, Math.floor(dataMin * 0.98 * 100) / 100) : 0);
  const span = Math.max(1e-9, max - min);
  const xFor = (t: number) => PAD.left + ((t - from) / Math.max(1, to - from)) * innerW;
  const yFor = (v: number) => PAD.top + innerH - ((v - min) / span) * innerH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => min + f * span);
  const xTicks = 8;
  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {band && <rect x={PAD.left} width={innerW} y={yFor(band.to)} height={Math.max(0, yFor(band.from) - yFor(band.to))} fill={band.color} />}
        {ticks.map(tv => (
          <g key={tv}>
            <line x1={PAD.left} x2={width - PAD.right} y1={yFor(tv)} y2={yFor(tv)} stroke="#e2e8f0" />
            <text x={PAD.left - 6} y={yFor(tv) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{format(tv)}</text>
          </g>
        ))}
        {Array.from({ length: xTicks + 1 }, (_, i) => from + (i / xTicks) * (to - from)).map(t => (
          <text key={t} x={xFor(t)} y={height - 10} textAnchor="middle" fontSize="10" fill="#64748b">{tickLabel(t)}</text>
        ))}
        {series.map(s => {
          const segs: string[] = [];
          let cur: string[] = [];
          s.points.forEach(p => { if (p.v === null) { if (cur.length) segs.push(cur.join(' ')); cur = []; } else cur.push(`${xFor(p.t)},${yFor(Math.min(max, Math.max(min, p.v)))}`); });
          if (cur.length) segs.push(cur.join(' '));
          return segs.map((seg, i) => (
            <polyline key={`${s.name}-${i}`} points={seg} fill="none" stroke={s.color} strokeWidth={s.width ?? 1.8} strokeDasharray={s.dashed ? '6 4' : undefined} strokeLinejoin="round" />
          ));
        })}
      </svg>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#475569', marginTop: 4, flexWrap: 'wrap' }}>
        {series.map(s => (
          <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 0, borderTop: `3px ${s.dashed ? 'dashed' : 'solid'} ${s.color}`, display: 'inline-block' }} />{s.name}
          </span>
        ))}
        {band && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, background: band.color, display: 'inline-block', borderRadius: 2 }} />{format(band.from)} – {format(band.to)}</span>}
      </div>
    </div>
  );
};

export interface TimelineSegment { start_ts: number; end_ts: number; state: string; reason: string | null; title: string }

export const STATE_COLORS: Record<string, string> = { prod: '#2f80ed', down: '#6b7280', down_short: '#e8590c', nodata: '#d1d5db' };

/** One 24h row per local day; segments coloured by state. */
export const DayTimeline: React.FC<{ days: { label: string; dayStart: number; segments: TimelineSegment[] }[]; shortStopSeconds: number }> = ({ days, shortStopSeconds }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', fontSize: 10, color: '#94a3b8' }}>
      <span />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>{[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => <span key={h}>{String(h).padStart(2, '0')}:00</span>)}</div>
    </div>
    {days.map(d => (
      <div key={d.label} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{d.label}</span>
        <div style={{ position: 'relative', height: 18, background: '#f8fafc', borderRadius: 4, overflow: 'hidden' }}>
          {d.segments.map((s, i) => {
            const left = ((s.start_ts - d.dayStart) / 86400) * 100;
            const w = ((s.end_ts - s.start_ts) / 86400) * 100;
            const color = s.state === 'down' && (s.reason === 'filament_lost' || s.end_ts - s.start_ts <= shortStopSeconds) ? STATE_COLORS.down_short : STATE_COLORS[s.state] ?? '#ccc';
            return <div key={i} title={s.title} style={{ position: 'absolute', left: `${left}%`, width: `${Math.max(w, 0.15)}%`, top: 0, bottom: 0, background: color }} />;
          })}
        </div>
      </div>
    ))}
  </div>
);
